import { z } from "zod";
export const CANONICAL_RESULT_SCHEMA_VERSION = "mlx.canonical-result.v1";
const decimalLexemeSchema = z.string().regex(/^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/u, "Decimal values must use plain base-10 notation without leading zeroes or exponents.");
export const canonicalDecimalValueSchema = z.object({
    kind: z.literal("decimal"),
    value: decimalLexemeSchema,
    scale: z.number().int().min(0).max(1000),
}).strict().superRefine((decimal, context) => {
    if (/^-0(?:\.0+)?$/u.test(decimal.value)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["value"], message: "Negative zero is not canonical." });
    }
    const fractionLength = decimal.value.includes(".")
        ? decimal.value.length - decimal.value.indexOf(".") - 1
        : 0;
    if (fractionLength !== decimal.scale) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["scale"],
            message: `Decimal scale ${decimal.scale} does not match ${fractionLength} fractional digits.`,
        });
    }
});
const canonicalIntegerValueSchema = z.object({
    kind: z.literal("integer"),
    value: z.string().regex(/^-?(?:0|[1-9][0-9]*)$/u),
}).strict().superRefine((integer, context) => {
    if (integer.value === "-0") {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["value"], message: "Negative zero is not canonical." });
    }
});
const canonicalTimestampValueSchema = z.object({
    kind: z.literal("timestamp"),
    value: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u),
}).strict().superRefine((timestamp, context) => {
    const parsed = new Date(timestamp.value);
    if (!Number.isFinite(parsed.valueOf()) || parsed.toISOString() !== timestamp.value) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["value"],
            message: "Timestamps must be valid UTC instants with millisecond precision.",
        });
    }
});
const canonicalDateValueSchema = z.object({
    kind: z.literal("date"),
    value: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
}).strict().superRefine((date, context) => {
    const parsed = new Date(`${date.value}T00:00:00.000Z`);
    if (!Number.isFinite(parsed.valueOf()) || parsed.toISOString().slice(0, 10) !== date.value) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["value"], message: "Dates must be valid calendar dates." });
    }
});
export const canonicalResultValueSchema = z.discriminatedUnion("kind", [
    canonicalDecimalValueSchema,
    canonicalIntegerValueSchema,
    z.object({ kind: z.literal("string"), value: z.string() }).strict(),
    z.object({ kind: z.literal("boolean"), value: z.boolean() }).strict(),
    canonicalTimestampValueSchema,
    canonicalDateValueSchema,
    z.object({
        kind: z.literal("missing"),
        reason: z.enum(["null", "not_available", "redacted"]),
    }).strict(),
]);
export const canonicalResultSchema = z.object({
    schemaVersion: z.literal(CANONICAL_RESULT_SCHEMA_VERSION),
    columns: z.array(z.string().trim().min(1).max(240)).min(1).max(1000),
    stableKey: z.array(z.string().trim().min(1).max(240)).min(1).max(100),
    rows: z.array(z.record(z.string(), canonicalResultValueSchema)).max(100_000),
}).strict().superRefine((result, context) => {
    const columns = new Set();
    for (const [index, column] of result.columns.entries()) {
        if (columns.has(column)) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["columns", index], message: `Duplicate column ${column}.` });
        }
        columns.add(column);
    }
    const stableColumns = new Set();
    for (const [index, column] of result.stableKey.entries()) {
        if (!columns.has(column)) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["stableKey", index], message: `Unknown stable-key column ${column}.` });
        }
        if (stableColumns.has(column)) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["stableKey", index], message: `Duplicate stable-key column ${column}.` });
        }
        stableColumns.add(column);
    }
    const seenKeys = new Set();
    for (const [rowIndex, row] of result.rows.entries()) {
        const rowColumns = Object.keys(row);
        for (const column of result.columns) {
            if (!(column in row)) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", rowIndex, column], message: `Missing column ${column}.` });
            }
        }
        for (const column of rowColumns) {
            if (!columns.has(column)) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", rowIndex, column], message: `Unexpected column ${column}.` });
            }
        }
        if (result.stableKey.every((column) => column in row)) {
            const key = result.stableKey.map((column) => canonicalCell(row[column])).join("\u0000");
            if (seenKeys.has(key)) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", rowIndex], message: "Duplicate stable key." });
            }
            seenKeys.add(key);
        }
    }
});
function canonicalCell(value) {
    switch (value.kind) {
        case "decimal": return `decimal:${value.scale}:${value.value}`;
        case "integer": return `integer:${value.value}`;
        case "string": return `string:${JSON.stringify(value.value)}`;
        case "boolean": return `boolean:${value.value ? "1" : "0"}`;
        case "timestamp": return `timestamp:${value.value}`;
        case "date": return `date:${value.value}`;
        case "missing": return `missing:${value.reason}`;
    }
}
function compareText(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
export function canonicalResultJson(input) {
    const parsed = canonicalResultSchema.parse(input);
    const stableColumns = new Set(parsed.stableKey);
    const valueColumns = parsed.columns.filter((column) => !stableColumns.has(column));
    const comparisonColumns = [...valueColumns, ...parsed.stableKey];
    const rows = parsed.rows.map((row) => Object.fromEntries(parsed.columns.map((column) => [column, row[column]]))).sort((left, right) => {
        for (const column of comparisonColumns) {
            const compared = compareText(canonicalCell(left[column]), canonicalCell(right[column]));
            if (compared !== 0)
                return compared;
        }
        return 0;
    });
    return JSON.stringify({
        schemaVersion: CANONICAL_RESULT_SCHEMA_VERSION,
        columns: parsed.columns,
        stableKey: parsed.stableKey,
        rows,
    });
}
export function canonicalResultBytes(input) {
    return new TextEncoder().encode(canonicalResultJson(input));
}
